<script setup lang="ts">
import { ArrowLeft, Camera, ClipboardCheck, ClipboardList, ListChecks } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { activeWebAppView, navigateBackInWebApp } from '../composables/webapp-navigation';
import WebAppConfirmationDialog from './WebAppConfirmationDialog.vue';

const props = withDefaults(defineProps<{
  title: string;
  icon?: 'survey' | 'evidence' | 'log' | 'pending';
  respectTopSafeArea?: boolean;
  stackedBelowOfflineRibbon?: boolean;
}>(), { icon: 'survey', respectTopSafeArea: true, stackedBelowOfflineRibbon: false });

const router = useRouter();
const confirmationOpen = ref(false);
const navigationLabel = computed(() => activeWebAppView.value ? 'Atrás' : 'Inicio');
const appIcon = computed(() => ({ survey: ClipboardCheck, evidence: Camera, log: ClipboardList, pending: ListChecks })[props.icon]);

async function handleBack() {
  if (activeWebAppView.value?.id === 'survey-form') {
    confirmationOpen.value = true;
    return;
  }
  if (await navigateBackInWebApp()) return;
  void router.push('/webapp');
}

function closeConfirmation() {
  confirmationOpen.value = false;
}

async function exitSurvey() {
  closeConfirmation();
  await navigateBackInWebApp();
}
</script>

<template>
  <header class="webapp-status-bar" :class="{ 'webapp-status-bar--safe-area': respectTopSafeArea, 'webapp-status-bar--below-offline': stackedBelowOfflineRibbon }">
    <div class="webapp-status-bar__inner">
      <button class="webapp-status-bar__back" type="button" :aria-label="activeWebAppView ? 'Volver a la vista anterior' : 'Volver a herramientas'" :aria-haspopup="activeWebAppView ? undefined : 'dialog'" @click="handleBack">
        <ArrowLeft :size="21" :stroke-width="2.2" aria-hidden="true" />
        <span>{{ navigationLabel }}</span>
      </button>
      <strong>{{ title }}</strong>
      <span class="webapp-status-bar__icon" :class="`webapp-status-bar__icon--${icon}`" aria-hidden="true"><component :is="appIcon" :size="21" :stroke-width="2" /></span>
    </div>
  </header>

  <WebAppConfirmationDialog
    :open="confirmationOpen"
    title="¿Seguro quieres salir de la encuesta?"
    description="La información que no hayas enviado se perderá."
    @cancel="closeConfirmation"
    @confirm="exitSurvey"
  />
</template>

<style scoped>
.webapp-status-bar {
  position: sticky;
  top: 0;
  z-index: 900;
  padding-right: env(safe-area-inset-right, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  color: #fff;
  background: rgba(16, 23, 42, .97);
  box-shadow: 0 1px 0 rgba(255, 255, 255, .08), 0 8px 24px rgba(8, 12, 25, .12);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
}
.webapp-status-bar--safe-area { padding-top: env(safe-area-inset-top, 0px); }
.webapp-status-bar--below-offline { top: calc(53px + env(safe-area-inset-top, 0px)); }
.webapp-status-bar__inner {
  width: min(980px, 100%);
  height: 56px;
  margin: auto;
  padding: 0 10px;
  display: grid;
  grid-template-columns: minmax(78px, 1fr) auto minmax(78px, 1fr);
  align-items: center;
  gap: 8px;
}
.webapp-status-bar__back {
  width: fit-content;
  min-height: 44px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 10px;
  color: #c9f17e;
  background: transparent;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.webapp-status-bar__back:active { background: rgba(255, 255, 255, .08); }
.webapp-status-bar__back:focus-visible { outline: 2px solid #b8ed5d; outline-offset: -2px; }
.webapp-status-bar strong {
  max-width: min(54vw, 360px);
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -.15px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.webapp-status-bar__icon { width:36px;height:36px;justify-self:end;display:grid;place-items:center;border-radius:11px; }
.webapp-status-bar__icon--survey { color:#315a21;background:#b8ed5d; }
.webapp-status-bar__icon--evidence { color:#3d2b7a;background:#c2b6f3; }
.webapp-status-bar__icon--log { color:#79511f;background:#f4bf64; }
.webapp-status-bar__icon--pending { color:#234b69;background:#b9def7; }
@media (max-width: 380px) {
  .webapp-status-bar__inner { grid-template-columns: minmax(65px, 1fr) auto minmax(65px, 1fr); }
  .webapp-status-bar__back { padding: 0 4px; }
}
.webapp-status-bar__back { font-size: var(--webapp-action-font-size); }
</style>
